import {Http} from '../../../Data/config/Http';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';
import {AppError} from '../../../Data/config/AppError';

// DTO placeholders - integrate with real models later
export interface InvoiceDTO { id: number; }
export interface SaleDTO { id: number; }
export interface AgingBucketDTO {
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  over_90: number;
  total_invoices?: number;
  total_due?: number;
}

export interface PaginatedDTO<T> {
  data: T[];
  pagination?: { page: number; limit: number; total: number; totalPages: number };
}

export type InvoiceFiltersDTO = {
  page?: number;
  limit?: number;
  status?: string;
  payment_status?: string;
  payment_method?: string;
  search?: string;
  store_id?: number | string;
  marketer_id?: number | string;
  due_from?: string;
  due_to?: string;
};

export const SalesDashboardAPIDataSource = {
  async getInvoices (params: InvoiceFiltersDTO): Promise<InvoiceDTO[]> {
    const url = API_ROUTES.SALES.INVOICES;
    try {
      const response = await Http.get<BaseResponse<InvoiceDTO[]>>(url, {params});
      return unwrapResponse(response.data, 'حدث خطأ في جلب الفواتير');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async updateInvoiceStatus (invoiceId: number | string, status: string): Promise<unknown> {
    const url = API_ROUTES.SALES.INVOICE_STATUS(invoiceId);
    try {
      const response = await Http.put<BaseResponse<unknown>>(url, {status});
      return unwrapResponse(response.data, 'حدث خطأ في تحديث حالة الفاتورة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async recordPayment (payload: {invoice_id: number; amount: number; payment_method: string; notes?: string}): Promise<unknown> {
    try {
      const response = await Http.post<BaseResponse<unknown>>(API_ROUTES.SALES.PAYMENTS_RECORD, payload);
      return unwrapResponse(response.data, 'حدث خطأ في تسجيل الدفعة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async getPendingBankTransfers (params: {page?: number; limit?: number}): Promise<any[]> {
    try {
      const response = await Http.get<BaseResponse<any[]>>(API_ROUTES.SALES.PENDING_BANK_TRANSFERS, {params});
      return unwrapResponse(response.data, 'حدث خطأ في جلب التحويلات البنكية المعلقة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async reviewBankTransfer (payload: {invoice_id: number; action: 'approved'|'rejected'; notes?: string}): Promise<unknown> {
    try {
      const response = await Http.post<BaseResponse<unknown>>(API_ROUTES.SALES.REVIEW_BANK_TRANSFER, payload);
      return unwrapResponse(response.data, 'حدث خطأ في مراجعة التحويل البنكي');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async getAgingReport (params: {storeId?: number; dateFrom?: string; dateTo?: string}): Promise<AgingBucketDTO> {
    try {
      const response = await Http.get<BaseResponse<AgingBucketDTO>>(API_ROUTES.SALES.AGING_REPORT, {params});
      return unwrapResponse(response.data, 'حدث خطأ في جلب تقرير الأعمار');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  // Invoice Attachment Methods
  async getInvoiceAttachment (invoiceId: string | number): Promise<InvoiceAttachmentDTO | null> {
    try {
      const response = await Http.get<BaseResponse<InvoiceAttachmentDTO | null>>(API_ROUTES.BILLING.INVOICE_ATTACHMENT(invoiceId.toString()));
      return unwrapResponse(response.data, 'حدث خطأ في جلب مرفق الفاتورة');
    } catch (error) {
      // Treat 404 as "no attachment" - return null instead of throwing
      if (error instanceof AppError && error.statusCode === 404) {
        return null;
      }
      handleDataSourceError(error);
    }
  },

  async uploadInvoiceAttachment (invoiceId: string | number, file: File): Promise<InvoiceAttachmentDTO> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await Http.post<BaseResponse<InvoiceAttachmentDTO>>(API_ROUTES.BILLING.INVOICE_ATTACHMENT(invoiceId.toString()), formData);
      return unwrapResponse(response.data, 'حدث خطأ في رفع مرفق الفاتورة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async replaceInvoiceAttachment (invoiceId: string | number, file: File): Promise<InvoiceAttachmentDTO> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await Http.put<BaseResponse<InvoiceAttachmentDTO>>(API_ROUTES.BILLING.INVOICE_ATTACHMENT(invoiceId.toString()), formData);
      return unwrapResponse(response.data, 'حدث خطأ في استبدال مرفق الفاتورة');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  async deleteInvoiceAttachment (invoiceId: string | number): Promise<void> {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.BILLING.INVOICE_ATTACHMENT(invoiceId.toString()));
      unwrapResponse(response.data, 'حدث خطأ في حذف مرفق الفاتورة', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};

// Invoice Attachment DTO (matches backend: permanent public S3 URL, no expires_at)
export interface InvoiceAttachmentDTO {
  original_filename: string;
  file_size: number;
  /** Permanent public download URL (non-expiring). Backend field: download_url */
  download_url: string;
  uploaded_at: string;
}


